import "react-phone-input-2/lib/style.css";
import "react-quill-new/dist/quill.snow.css";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid2,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import ReactQuill from "react-quill-new";

import useI18N from "../hooks/useI18N";
import theme from "../themes/theme";
import MUIFormHelperText from "./MUIFormHelperText";
import MUIFormLabel from "./MUIFormLabel";
import MUISelect from "./MUISelect";
import { MUIStyled } from "./MUIStyled";
import MUITooltip from "./MUITooltip";
import SABox from "./SABox";
import SATextField from "./SATextField";

/**
 * @param {object} onChangeHandlers - Custom onChange handler for individual fields (based on name)
 */

const StyledAutocomplete = MUIStyled(Autocomplete)(({ dir }) => {
  const isRTL = dir === "rtl";
  return {
    "& .MuiInputBase-root": {
      padding: isRTL
        ? "8px 8px 8px 56px !important"
        : "8px 56px 8px 8px !important",
      "& .MuiInputBase-input": { "&.MuiAutocomplete-input": { padding: 0 } },
      "& .MuiButtonBase-root": { flexDirection: isRTL ? "row-reverse" : "" },
      "& .MuiAutocomplete-endAdornment": {
        right: isRTL ? "auto" : 9,
        left: isRTL ? 9 : "auto",
      },
    },
  };
});

const StyledPhoneInput = MUIStyled(PhoneInput)(({ dir }) => {
  const isRTL = dir === "rtl";
  return {
    "&.react-tel-input": {
      "& .form-control": {
        width: "100%",
        height: "40px",
        borderRadius: 0,
        paddingLeft: isRTL ? 10 : 48,
        paddingRight: isRTL ? 48 : 0,
        textAlign: isRTL ? "right" : "left",
      },
      "& .flag-dropdown": {
        background: "transparent",
        borderColor: "rgb(157 164 174)",
        borderRight: "none",
        borderRadius: 0,
        "&:hover": { borderColor: "#B0B0B0" },
        "&.open": {
          background: "transparent",
          borderColor: "rgb(157 164 174)",
        },
        "& .selected-flag": {
          padding: isRTL ? "0 8px 0 8px" : "0 0 0 8px",
          backgroundColor: "transparent",
          "&:hover, &:focus": { backgroundColor: "transparent" },
          "& .arrow": { left: isRTL ? "auto" : 20, right: isRTL ? 20 : "auto" },
        },
      },
    },
  };
});

function getNested(obj, path) {
  return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
}

const SAFormFields = ({
  fields,
  control,
  register,
  formState,
  onChangeHandlers = {},
  isDisabled = false,
  spacing,
}) => {
  const { t, dir } = useI18N();
  const [visible, setVisible] = useState({});
  const { errors } = formState;
  const { currentLang } = useI18N();

  const toggleVisible = (name) => {
    setVisible((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const renderField = (field) => {
    const {
      type,
      name,
      size,
      options,
      placeholder,
      formLabel,
      tooltip,
      defaultValue,
      registerOptions,
      startAdornment,
      endAdornment,
      isMultiple = false,
      disabled = false,
      isArabic = false,
      isEnglish = false,
      ...rest
    } = field;

    const errorObj = getNested(errors, name);

    const inputDir = isArabic
      ? "rtl"
      : isEnglish
        ? "ltr"
        : currentLang === "ar"
          ? "rtl"
          : "ltr";

    const textAlign = isArabic
      ? "right"
      : isEnglish
        ? "left"
        : currentLang === "ar"
          ? "right"
          : "left";

    switch (type) {
      case "select":
        return (
          <Grid2 size={size} key={name}>
            <Controller
              name={name}
              control={control}
              rules={{ ...registerOptions }}
              shouldUnregister
              render={({ field }) => (
                <MUISelect
                  startAdornment={startAdornment}
                  {...{
                    options,
                    id: name,
                    defaultValue,
                    formLabel: (
                      <SABox display="flex" alignItems="center" gap={0.5}>
                        {registerOptions?.required?.value && "*"} {t(formLabel)}
                        {tooltip && (
                          <MUITooltip title={t(tooltip)}>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 16,
                                color: "#6C737F",
                                cursor: "pointer",
                              }}
                            />
                          </MUITooltip>
                        )}
                      </SABox>
                    ),
                    placeholder: t(placeholder),
                    onChange: onChangeHandlers[name],
                    error: t(errors[name]?.message),
                    disabled: disabled || isDisabled,
                    ...field,
                  }}
                />
              )}
            />
          </Grid2>
        );

      case "phone":
        return (
          <Grid2 size={size} key={name}>
            <Controller
              name={name}
              control={control}
              rules={{ ...registerOptions }}
              shouldUnregister
              render={({ field: { value, onChange } }) => (
                <FormControl fullWidth>
                  <MUIFormLabel>
                    {registerOptions?.required?.value && "*"} {t(formLabel)}
                  </MUIFormLabel>
                  <StyledPhoneInput
                    autoFormat
                    enableSearch
                    country={"bh"}
                    copyNumbersOnly
                    value={value}
                    disabled={disabled}
                    onChange={(value, data) => {
                      onChange(value);
                      if (onChangeHandlers[name]) {
                        onChangeHandlers[name](value, data);
                      }
                    }}
                    dir={dir}
                    inputComponent={TextField}
                    inputStyle={{
                      height: 40,
                      width: "100%",
                      textAlign,
                      direction: inputDir,
                      borderColor: errors[name]?.message
                        ? "#d32f2f"
                        : theme.palette.gray.main,
                      color: disabled ? theme.palette.text.disabled : "black",
                    }}
                  />

                  {errors[name]?.message ? (
                    <MUIFormHelperText>
                      {t(errors[name]?.message)}
                    </MUIFormHelperText>
                  ) : (
                    <MUIFormHelperText error={false}>
                      {t(rest?.helperText)}
                    </MUIFormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid2>
        );

      case "autocomplete":
        return (
          <Grid2 size={size} key={name}>
            <Controller
              name={name}
              control={control}
              rules={{ ...registerOptions }}
              shouldUnregister
              render={({ field: { value, onChange } }) => {
                // max selection logic
                const max = field.max;
                const handleChange = (_, newValue) => {
                  if (
                    isMultiple &&
                    typeof max === "number" &&
                    newValue?.length > max
                  ) {
                    // Only allow up to max selections
                    return;
                  }
                  const updatedValue = isMultiple
                    ? newValue?.map((item) => item.value)
                    : newValue?.value || "";
                  onChange(updatedValue);
                  if (onChangeHandlers[name]) {
                    onChangeHandlers[name](updatedValue);
                  }
                };
                return (
                  <FormControl fullWidth>
                    <MUIFormLabel>
                      {registerOptions?.required?.value && "*"} {t(formLabel)}
                    </MUIFormLabel>
                    <StyledAutocomplete
                      multiple={isMultiple}
                      disabled={isDisabled}
                      dir={dir}
                      options={options?.map((option) => ({
                        ...option,
                        label: t(option.label),
                      }))}
                      getOptionLabel={(option) => t(option.label) || ""}
                      value={
                        isMultiple
                          ? options?.filter((option) =>
                              value?.includes(option.value),
                            )
                          : options?.find((option) => option.value === value) ||
                            null
                      }
                      onChange={handleChange}
                      renderOption={(props, option) => (
                        <li {...props} key={option.value}>
                          {option.label}
                        </li>
                      )}
                      renderInput={(params) => (
                        <SATextField
                          {...params}
                          placeholder={t(placeholder)}
                          error={errors[name]?.message}
                          helperText={
                            errors[name]?.message && t(errors[name]?.message)
                          }
                          inputProps={{
                            ...params.inputProps,
                            dir: inputDir,
                            style: {
                              textAlign,
                              cursor: "pointer",
                            },
                          }}
                        />
                      )}
                      ListboxComponent={(props) => {
                        const listRef = useRef();
                        const handleScroll = rest.onScroll || field.onScroll;
                        useEffect(() => {
                          if (listRef.current) {
                            listRef.current.scrollTop =
                              listRef.current.scrollHeight;
                          }
                        }, [options?.length]);

                        return (
                          <ul
                            ref={listRef}
                            {...props}
                            style={{ maxHeight: 250, overflowY: "auto" }}
                            onScroll={handleScroll}
                          />
                        );
                      }}
                      {...rest}
                    />
                  </FormControl>
                );
              }}
            />
          </Grid2>
        );

      case "radio":
        return (
          <Grid2 size={size} key={name}>
            <Controller
              name={name}
              control={control}
              rules={{ ...registerOptions }}
              defaultValue={defaultValue}
              shouldUnregister
              render={({ field }) => (
                <FormControl component="fieldset">
                  <MUIFormLabel component="legend">{t(formLabel)}</MUIFormLabel>
                  <RadioGroup
                    row
                    {...field}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      if (onChangeHandlers[name]) {
                        onChangeHandlers[name](e.target.value);
                      }
                    }}
                  >
                    {options.map((option) => (
                      <FormControlLabel
                        disabled={disabled || option.disabled}
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={t(option.label)}
                      />
                    ))}
                  </RadioGroup>
                  {errors[name]?.message && (
                    <MUIFormHelperText>
                      {t(errors[name]?.message)}
                    </MUIFormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid2>
        );

      case "checkbox":
        return (
          <Grid2 size={size} key={name}>
            <Controller
              name={name}
              control={control}
              rules={{ ...registerOptions }}
              defaultValue={defaultValue || false}
              shouldUnregister
              render={({ field }) => (
                <FormControl component="fieldset" error={!!errors[name]}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                        disabled={disabled || isDisabled}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (onChangeHandlers[name]) {
                            onChangeHandlers[name](e.target.checked);
                          }
                        }}
                        sx={{
                          "&.Mui-checked": {
                            color: "#C5A56F",
                          },
                        }}
                      />
                    }
                    label={
                      <SABox display="flex" alignItems="center" gap={0.5}>
                        {registerOptions?.required?.value && "*"} {t(formLabel)}
                        {tooltip && (
                          <MUITooltip title={t(tooltip)}>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 16,
                                color: "#6C737F",
                                cursor: "pointer",
                              }}
                            />
                          </MUITooltip>
                        )}
                      </SABox>
                    }
                  />
                  {errors[name]?.message && (
                    <MUIFormHelperText>
                      {t(errors[name]?.message)}
                    </MUIFormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid2>
        );

      case "textarea":
        return (
          <Grid2 size={size} key={name}>
            <Controller
              name={name}
              control={control}
              rules={{ ...registerOptions }}
              shouldUnregister
              render={({ field: { value, onChange } }) => (
                <FormControl fullWidth>
                  <MUIFormLabel>
                    {registerOptions?.required?.value && "*"} {t(formLabel)}
                    {tooltip && (
                      <MUITooltip title={t(tooltip)}>
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: "#6C737F",
                            cursor: "pointer",
                            ml: 0.5,
                          }}
                        />
                      </MUITooltip>
                    )}
                  </MUIFormLabel>

                  <SABox
                    sx={{
                      "& .ql-container": {
                        minHeight: "200px",
                        maxHeight: "300px",
                        overflowY: "auto",
                        backgroundColor:
                          disabled || isDisabled ? "#f5f5f5" : "#fff",
                      },
                      "& .ql-editor": {
                        minHeight: "200px",
                        "& h1": { fontSize: "2rem !important" },
                        "& h2": { fontSize: "1.75rem !important" },
                        "& h3": { fontSize: "1.5rem !important" },
                        "& h4": { fontSize: "1.25rem !important" },
                        "& h5": { fontSize: "1.1rem !important" },
                        "& h6": { fontSize: "1rem !important" },
                      },
                      border:
                        errors[name]?.message || errorObj?.message
                          ? "1px solid #d32f2f"
                          : `1px solid ${theme.palette.gray.main}`,
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={value || ""}
                      onChange={(val) => {
                        onChange(val);
                        if (onChangeHandlers[name]) onChangeHandlers[name](val);
                      }}
                      placeholder={t(placeholder)}
                      readOnly={disabled || isDisabled}
                      style={{
                        border: "none",
                      }}
                      dir={inputDir}
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, 4, 5] }],
                          // [{ font: [] }], // Font family
                          // [{ size: [] }], // Font sizes
                          [{ list: "bullet" }, { list: "ordered" }],
                          ["bold", "italic", "underline", "strike"], // Basic formatting
                          [{ color: [] }, { background: [] }], // Text & background color
                          // [{ align: [] }], // Alignment
                          [{ direction: "rtl" }],
                        ],
                        clipboard: {
                          matchVisual: false, // ✅ keeps original styles when pasting
                        },
                      }}
                    />
                  </SABox>

                  {errors[name]?.message && (
                    <MUIFormHelperText>
                      {t(errors[name]?.message)}
                    </MUIFormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid2>
        );

      default:
        return (
          <Grid2 size={size} key={name}>
            <SATextField
              {...{
                id: name,
                placeholder: t(placeholder),
                type: visible[name] ? "text" : type,
                // formLabel: t(formLabel),
                formLabel: (
                  <SABox display="flex" alignItems="center" gap={0.5}>
                    {registerOptions?.required?.value && "*"} {t(formLabel)}
                    {tooltip && (
                      <MUITooltip title={t(tooltip)}>
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: "#6C737F",
                            cursor: "pointer",
                          }}
                        />
                      </MUITooltip>
                    )}
                  </SABox>
                ),
                error: errors[name]?.message || errorObj?.message,
                ...register(name, {
                  shouldUnregister: true,
                  onChange: onChangeHandlers[name],
                  ...registerOptions,
                }),
                disabled: disabled || isDisabled,
                ...rest,
                InputProps: {
                  startAdornment,
                  endAdornment:
                    type === "password" ? (
                      visible[name] ? (
                        <VisibilityOutlinedIcon
                          onClick={() => toggleVisible(name)}
                          style={{ cursor: "pointer" }}
                        />
                      ) : (
                        <VisibilityOffOutlinedIcon
                          onClick={() => toggleVisible(name)}
                          style={{ cursor: "pointer" }}
                        />
                      )
                    ) : (
                      endAdornment
                    ),
                },
                inputProps: {
                  dir: inputDir,
                  style: {
                    textAlign,
                  },
                  ...(type === "number"
                    ? {
                        step: "any",
                        onWheel: (e) => e.target.blur(), // 👈 prevents scroll value change
                      }
                    : {}),
                },
              }}
            />
          </Grid2>
        );
    }
  };

  return (
    <Grid2 container spacing={spacing}>
      {fields.map((field) => renderField(field))}
    </Grid2>
  );
};

export default SAFormFields;
